import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Tooltip } from "primereact/tooltip";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import CustomDropdown from "../../custom/customDropdown";
import { AuthContext } from "../../Header/auth - context";
import "./Fakture.css";

const toUtcIso = (d) => {
    if (!d) return null;
    const x = d instanceof Date ? d : new Date(d);
    const utc = new Date(x.getTime() - x.getTimezoneOffset() * 60000);
    return utc.toISOString().slice(0, 19) + "Z";
};

const iznos = (v) =>
    v === null || v === undefined || v === ""
        ? "0,00"
        : Number(v).toLocaleString("sr-RS", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const praznoZaglavlje = {
    id: null, broj: null, godina: new Date().getFullYear(), datum: new Date(),
    id_klijent: null, id_klijent_druga_strana: null, valuta: null, kurs: null,
    brojUgovora: "", statusKompenzacije: null, statusNaziv: "", datumPotpisano: null,
    datumIzvrseno: null, napomena: "", zakljucan: false, brojStrana: 2,
};

const Kompenzacije = () => {
    const { t } = useTranslation();
    const auth = useContext(AuthContext);
    const company = useSelector((state) => state.company);
    const toast = useRef(null);

    const [prikaz, setPrikaz] = useState("PREGLED");   // PREGLED | DOKUMENT
    const [sifarnici, setSifarnici] = useState({});

    /* pregled */
    const [lista, setLista] = useState([]);
    const [godina, setGodina] = useState(new Date().getFullYear());
    const [filterStatus, setFilterStatus] = useState(null);
    const [globalFilter, setGlobalFilter] = useState(null);
    const [ucitava, setUcitava] = useState(false);

    /* jedna kompenzacija */
    const [zaglavlje, setZaglavlje] = useState({ ...praznoZaglavlje });
    const [parovi, setParovi] = useState([]);
    const [cuva, setCuva] = useState(false);

    /* biranje para: leva strana (naše izlazne) i desna (ulazne) */
    const [potrazujemo, setPotrazujemo] = useState([]);
    const [dugujemo, setDugujemo] = useState([]);
    const [izborPotrazujemo, setIzborPotrazujemo] = useState(null);
    const [izborDugujemo, setIzborDugujemo] = useState(null);
    const [iznosPara, setIznosPara] = useState(null);
    const [ucitavaStrane, setUcitavaStrane] = useState(false);

    const poruka = (severity, summary, detail) =>
        toast.current?.show({ severity, summary, detail, life: 5000 });

    const posalji = async (putanja, telo = {}) => {
        const r = await fetch(process.env.REACT_APP_BACKEND_URL + "/kompenzacija/" + putanja, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
            body: JSON.stringify(telo),
        });
        const tekst = await r.text();
        const d = tekst ? JSON.parse(tekst) : {};
        if (!r.ok) throw new Error(d.message || t("common.greska"));
        return d;
    };

    const greska = (e) => poruka("error", t("common.greska"), e.message);

    useEffect(() => {
        posalji("Init").then(setSifarnici).catch(() => {});
        // eslint-disable-next-line
    }, []);

    const ucitajPregled = () => {
        setUcitava(true);
        posalji("Pregled", { godina, status: filterStatus })
            .then((d) => setLista(d || []))
            .catch(greska)
            .finally(() => setUcitava(false));
    };

    useEffect(() => { ucitajPregled(); /* eslint-disable-next-line */ }, []);

    /* Izvršena kompenzacija je zaključana — parovi su upisani u knjigu veza. */
    const zakljucana = !!zaglavlje.zakljucan;

    const ucitajStrane = (zagl) => {
        if (!zagl?.id_klijent) { setPotrazujemo([]); setDugujemo([]); return; }
        setUcitavaStrane(true);
        /* Kod tropartitne dugujemo DRUGOJ strani; kod dvostrane istom partneru. */
        const klijentDuga = zagl.id_klijent_druga_strana || zagl.id_klijent;
        Promise.all([
            posalji("Otvorene", { strana: "POTRAZUJEMO", id_klijent: zagl.id_klijent, valuta: zagl.valuta }),
            posalji("Otvorene", { strana: "DUGUJEMO", id_klijent: klijentDuga, valuta: zagl.valuta }),
        ])
            .then(([p, d]) => { setPotrazujemo(p || []); setDugujemo(d || []); })
            .catch(greska)
            .finally(() => setUcitavaStrane(false));
    };

    const otvoriKompenzaciju = (id) => {
        posalji("Get", { id })
            .then((d) => {
                const z = d.zaglavlje || {};
                const novo = {
                    id: z.id, broj: z.Broj, godina: z.Godina,
                    datum: z.Datum ? new Date(z.Datum) : new Date(),
                    id_klijent: z.id_klijent, id_klijent_druga_strana: z.id_klijent_druga_strana,
                    valuta: z.Valuta, kurs: z.Kurs, brojUgovora: z.BrojUgovora || "",
                    statusKompenzacije: z.StatusKompenzacije, statusNaziv: z.StatusNaziv || "",
                    datumPotpisano: z.DatumPotpisano ? new Date(z.DatumPotpisano) : null,
                    datumIzvrseno: z.DatumIzvrseno ? new Date(z.DatumIzvrseno) : null,
                    napomena: z.Napomena || "", zakljucan: !!z.Zakljucan, brojStrana: z.BrojStrana || 2,
                };
                setZaglavlje(novo);
                setParovi(d.parovi || []);
                setIzborPotrazujemo(null); setIzborDugujemo(null); setIznosPara(null);
                ucitajStrane(novo);
                setPrikaz("DOKUMENT");
            })
            .catch(greska);
    };

    const nova = () => {
        setZaglavlje({ ...praznoZaglavlje });
        setParovi([]); setPotrazujemo([]); setDugujemo([]);
        setIzborPotrazujemo(null); setIzborDugujemo(null); setIznosPara(null);
        setPrikaz("DOKUMENT");
        posalji("NoviBroj", { godina: new Date().getFullYear() })
            .then((d) => setZaglavlje((p) => ({ ...p, broj: d.Broj })))
            .catch(() => {});
    };

    const sacuvaj = () => {
        setCuva(true);
        posalji("Save", {
            ...zaglavlje,
            datum: toUtcIso(zaglavlje.datum),
            datumPotpisano: toUtcIso(zaglavlje.datumPotpisano),
        })
            .then((d) => {
                poruka("success", t("common.sacuvano"), d.message);
                const idNovi = d.ID || zaglavlje.id;
                setZaglavlje((p) => ({ ...p, id: idNovi }));
                ucitajStrane({ ...zaglavlje, id: idNovi });
                ucitajPregled();
            })
            .catch(greska)
            .finally(() => setCuva(false));
    };

    const osvezi = (id) =>
        posalji("Get", { id: id || zaglavlje.id })
            .then((d) => {
                setParovi(d.parovi || []);
                const z = d.zaglavlje || {};
                setZaglavlje((p) => ({
                    ...p,
                    statusKompenzacije: z.StatusKompenzacije, statusNaziv: z.StatusNaziv || "",
                    zakljucan: !!z.Zakljucan,
                    datumIzvrseno: z.DatumIzvrseno ? new Date(z.DatumIzvrseno) : null,
                }));
                ucitajStrane({ ...zaglavlje, ...z, id_klijent: z.id_klijent, id_klijent_druga_strana: z.id_klijent_druga_strana });
            })
            .catch(() => {});

    /* ── par ── */
    const dodajPar = () => {
        if (!zaglavlje.id) { poruka("warn", t("common.upozorenje"), t("komp.prvoSacuvaj")); return; }
        if (!izborPotrazujemo || !izborDugujemo) {
            poruka("warn", t("common.upozorenje"), t("komp.izaberiObeFakture")); return;
        }
        if (!iznosPara || Number(iznosPara) <= 0) {
            poruka("warn", t("common.upozorenje"), t("komp.unesiIznos")); return;
        }
        posalji("VezaSave", {
            id_kompenzacija: zaglavlje.id,
            id_faktura_potrazujemo: izborPotrazujemo.id_faktura,
            id_faktura_dugujemo: izborDugujemo.id_faktura,
            iznos: Number(iznosPara),
        })
            .then((d) => {
                poruka("success", t("common.sacuvano"), d.message);
                setIzborPotrazujemo(null); setIzborDugujemo(null); setIznosPara(null);
                osvezi();
            })
            .catch(greska);
    };

    const obrisiPar = (red) => {
        confirmDialog({
            message: t("komp.potvrdaBrisanjaPara"),
            header: t("komp.brisanjePara"),
            icon: "pi pi-exclamation-triangle",
            acceptLabel: t("common.da"), rejectLabel: t("common.ne"),
            accept: () => posalji("VezaDelete", { id: red.id })
                .then((d) => { poruka("success", t("common.sacuvano"), d.message); osvezi(); })
                .catch(greska),
        });
    };

    /* Predlog iznosa: koliko se najviše može prebiti ovim parom — manji od dva
       raspoloživa. Operater ga sme smanjiti, preko toga procedura ne pušta. */
    const predlozeniIznos = useMemo(() => {
        if (!izborPotrazujemo || !izborDugujemo) return null;
        return Math.min(Number(izborPotrazujemo.Raspolozivo || 0), Number(izborDugujemo.Raspolozivo || 0));
    }, [izborPotrazujemo, izborDugujemo]);

    useEffect(() => { setIznosPara(predlozeniIznos); }, [predlozeniIznos]);

    const zbirParova = useMemo(
        () => (parovi || []).reduce((s, r) => s + Number(r.Iznos || 0), 0), [parovi]);

    const izvrsi = () => {
        confirmDialog({
            message: t("komp.potvrdaIzvrsenja"),
            header: t("komp.izvrsi"),
            icon: "pi pi-check-circle",
            acceptLabel: t("common.da"), rejectLabel: t("common.ne"),
            accept: () => posalji("Izvrsi", { id: zaglavlje.id })
                .then((d) => { poruka("success", t("komp.izvrsi"), d.message); osvezi(); ucitajPregled(); })
                .catch(greska),
        });
    };

    const ponisti = () => {
        confirmDialog({
            message: t("komp.potvrdaPonistenja"),
            header: t("komp.ponisti"),
            icon: "pi pi-undo",
            acceptLabel: t("common.da"), rejectLabel: t("common.ne"),
            accept: () => posalji("Ponisti", { id: zaglavlje.id })
                .then((d) => { poruka("success", t("komp.ponisti"), d.message); osvezi(); ucitajPregled(); })
                .catch(greska),
        });
    };

    const obrisi = () => {
        confirmDialog({
            message: t("komp.potvrdaBrisanja"),
            header: t("komp.brisanje"),
            icon: "pi pi-exclamation-triangle",
            acceptLabel: t("common.da"), rejectLabel: t("common.ne"),
            accept: () => posalji("Delete", { id: zaglavlje.id })
                .then((d) => { poruka("success", t("common.sacuvano"), d.message); setPrikaz("PREGLED"); ucitajPregled(); })
                .catch(greska),
        });
    };

    /* ── Izjava o kompenzaciji ── */
    const stampaj = (idKomp) => {
        posalji("Stampa", { id: idKomp || zaglavlje.id })
            .then((d) => {
                const z = d.zaglavlje;
                if (!z) return;
                const redovi = (d.parovi || []).map((r) => `
                    <tr>
                        <td class="c">${r.RB}</td>
                        <td>${r.BrojPotrazujemo || ""}<br><span class="s">${r.PartnerPotrazujemo || ""}</span></td>
                        <td class="c">${r.DatumPotrazujemo || ""}</td>
                        <td>${r.BrojDugujemo || ""}<br><span class="s">${r.PartnerDugujemo || ""}</span></td>
                        <td class="c">${r.DatumDugujemo || ""}</td>
                        <td class="d">${iznos(r.Iznos)}</td>
                    </tr>`).join("");

                const strane = [
                    `<p><b>1.</b> ${z.NasaFirma || ""}, ${z.NasaAdresa || ""}, ${t("sefUlazne.pib")}: ${z.NasPIB || "–"}</p>`,
                    `<p><b>2.</b> ${z.Partner || ""}, ${z.PartnerAdresa || ""}, ${t("sefUlazne.pib")}: ${z.PartnerPIB || "–"}</p>`,
                    z.DrugaStrana
                        ? `<p><b>3.</b> ${z.DrugaStrana}, ${z.DrugaStranaAdresa || ""}, ${t("sefUlazne.pib")}: ${z.DrugaStranaPIB || "–"}</p>`
                        : "",
                ].join("");

                const potpisi = [z.NasaFirma, z.Partner, z.DrugaStrana]
                    .filter(Boolean)
                    .map((ime) => `<div class="potpis"><div class="linija"></div>${ime}</div>`).join("");

                const html = `
<!doctype html><html lang="sr"><head><meta charset="utf-8">
<title>${t("komp.izjavaNaslov")} ${z.Broj || ""}/${z.Godina || ""}</title>
<style>
  body { font-family: "Segoe UI", Arial, sans-serif; color:#111; margin:18mm 14mm; }
  h1 { font-size:15pt; margin:0 0 2px; text-align:center; }
  .pod { text-align:center; font-size:10pt; margin:0 0 14px; }
  .strane p { margin:2px 0; font-size:10pt; }
  table { border-collapse:collapse; width:100%; font-size:9pt; margin-top:12px; }
  th, td { border:1px solid #333; padding:4px 5px; vertical-align:top; }
  th { background:#eee; font-weight:700; }
  td.d { text-align:right; font-variant-numeric:tabular-nums; white-space:nowrap; }
  td.c { text-align:center; white-space:nowrap; }
  span.s { color:#555; font-size:8pt; }
  tfoot td { font-weight:700; background:#f4f4f4; }
  .napomena { font-size:9pt; margin-top:10px; }
  .potpisi { display:flex; justify-content:space-between; gap:20px; margin-top:28mm; }
  .potpis { flex:1; text-align:center; font-size:9pt; }
  .potpis .linija { border-top:1px solid #333; margin-bottom:4px; }
  @media print { body { margin:10mm; } }
</style></head><body>
  <h1>${t("komp.izjavaNaslov")}</h1>
  <p class="pod">${t("komp.broj")} ${z.Broj || ""}/${z.Godina || ""} &nbsp;·&nbsp; ${z.Datum || ""}
     ${z.BrojUgovora ? `&nbsp;·&nbsp; ${t("komp.brojUgovora")}: ${z.BrojUgovora}` : ""}</p>
  <div class="strane"><p><b>${t("komp.ucesnici")}:</b></p>${strane}</div>
  <table>
    <thead><tr>
      <th>#</th><th>${t("komp.potrazujemoKolona")}</th><th>${t("izvod.datum")}</th>
      <th>${t("komp.dugujemoKolona")}</th><th>${t("izvod.datum")}</th><th>${t("komp.iznos")}</th>
    </tr></thead>
    <tbody>${redovi}</tbody>
    <tfoot><tr><td colspan="5">${t("komp.ukupnoPrebijeno")}</td>
      <td class="d">${iznos(z.UkupnoPrebijeno)}</td></tr></tfoot>
  </table>
  ${z.Napomena ? `<p class="napomena">${z.Napomena}</p>` : ""}
  <div class="potpisi">${potpisi}</div>
</body></html>`;

                const w = window.open("", "_blank");
                if (!w) { poruka("warn", t("common.upozorenje"), t("kartica.popupBlokiran")); return; }
                w.document.write(html);
                w.document.close();
                w.focus();
                w.print();
            })
            .catch(greska);
    };

    /* ── Excel pregleda ── */
    const exportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Kompenzacije");
        const headers = [
            t("komp.broj"), t("izvod.godina"), t("izvod.datum"), t("izvod.klijent"),
            t("komp.drugaStrana"), t("komp.status"), t("komp.parova"), t("komp.iznos"),
        ];
        const headerRow = worksheet.addRow(headers);
        headerRow.font = { bold: true };
        headerRow.eachCell((cell) => {
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFEFEFEF" } };
            cell.border = { top: { style: "medium" }, left: { style: "medium" }, bottom: { style: "medium" }, right: { style: "medium" } };
        });
        (lista || []).forEach((r) => {
            const row = worksheet.addRow([
                r.Broj, r.Godina, r.Datum || "", r.Klijent || "", r.DrugaStrana || "",
                r.StatusNaziv || "", r.Parova, Number(r.Iznos || 0),
            ]);
            row.eachCell((cell) => {
                cell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
            });
        });
        worksheet.columns.forEach((col) => {
            let max = 10;
            col.eachCell((cell) => { if (cell.value) max = Math.max(max, cell.value.toString().length); });
            col.width = max + 2;
        });
        const dt = new Date().toLocaleString("sr-RS", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Belgrade" }).replace(/,/g, "");
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
            `${t("komp.naslov")} - ${company?.companyName || ""} - ${dt}.xlsx`);
    };

    const headerPregleda = (
        <div className="flex align-items-center gap-2 export-buttons" style={{ display: "flex", justifyContent: "space-between" }}>
            <div style={{ display: "flex", gap: "0.4rem" }}>
                <Button type="button" icon="pi pi-file-excel" severity="success" rounded
                    onClick={exportExcel} data-pr-tooltip="XLS" />
            </div>
            <input type="search" placeholder={t("nova.phPretraga")} className="p-inputtext p-component"
                onInput={(e) => setGlobalFilter(e.target.value)} />
        </div>
    );

    const footerParova = (
        <ColumnGroup>
            <Row>
                <Column footer={t("komp.ukupnoPrebijeno")} colSpan={4} footerStyle={{ textAlign: "right" }} />
                <Column footer={iznos(zbirParova)} footerStyle={{ textAlign: "right" }} />
                <Column footer="" colSpan={2} />
            </Row>
        </ColumnGroup>
    );

    // ══════════════════════════ PREGLED ══════════════════════════
    if (prikaz === "PREGLED") {
        return (
            <div className="col-12">
                <Toast ref={toast} /><ConfirmDialog />
                <Tooltip target=".export-buttons>button" position="bottom" />
                <div className="faktureStranica">
                    <Card>
                        <div className="sef-ulazne-filter">
                            <div className="sef-filter-polje">
                                <label>{t("izvod.godina")}:</label>
                                <InputNumber value={godina} onValueChange={(e) => setGodina(e.value)}
                                    useGrouping={false} min={2000} max={2100} />
                            </div>
                            <div className="sef-filter-polje">
                                <label>{t("komp.status")}:</label>
                                <CustomDropdown value={filterStatus} optionLabel="label" showClear
                                    options={sifarnici.StatusiDB || []}
                                    placeholder={t("komp.sviStatusi")}
                                    onChange={(e) => setFilterStatus(e.target.value)} />
                            </div>
                            <div className="sef-filter-dugmad">
                                <Button type="button" label={t("sefUlazne.ucitaj")} icon="pi pi-search"
                                    className="p-button-sm" loading={ucitava} onClick={ucitajPregled} />
                                <Button type="button" label={t("komp.nova")} icon="pi pi-plus"
                                    className="p-button-sm p-button-success" onClick={nova} />
                            </div>
                        </div>
                    </Card>

                    <Card>
                        <DataTable value={lista || []} loading={ucitava}
                            paginator rows={25} rowsPerPageOptions={[25, 50, 100]}
                            globalFilter={globalFilter} header={headerPregleda}
                            scrollable scrollHeight="55vh" size="small"
                            resizableColumns columnResizeMode="expand"
                            emptyMessage={t("common.noDataTable")}
                            className="turePregledTable"
                            onRowDoubleClick={(e) => otvoriKompenzaciju(e.data.id)}>
                            <Column header="" style={{ width: "190px" }}
                                body={(r) => (
                                    <div style={{ display: "flex", gap: "0.2rem" }}>
                                        <Button type="button" label={t("kartica.otvori")} icon="pi pi-eye"
                                            className="p-button-text p-button-sm" onClick={() => otvoriKompenzaciju(r.id)} />
                                        {/* Izjava se štampa i bez otvaranja dokumenta — najčešće se
                                            traži baš sa spiska, za potpis ili ponovno slanje. */}
                                        <Button type="button" icon="pi pi-print"
                                            className="p-button-text p-button-sm"
                                            tooltip={t("komp.stampa")} tooltipOptions={{ position: "bottom" }}
                                            onClick={() => stampaj(r.id)} />
                                    </div>
                                )} />
                            <Column field="Broj" header={t("komp.broj")} style={{ minWidth: "90px" }} />
                            <Column field="Godina" header={t("izvod.godina")} style={{ minWidth: "80px" }} />
                            <Column field="Datum" header={t("izvod.datum")} style={{ minWidth: "110px" }} />
                            <Column field="Klijent" header={t("izvod.klijent")} style={{ minWidth: "200px" }} />
                            <Column field="DrugaStrana" header={t("komp.drugaStrana")} style={{ minWidth: "200px" }}
                                body={(r) => r.DrugaStrana || "—"} />
                            <Column field="StatusNaziv" header={t("komp.status")} style={{ minWidth: "120px" }} />
                            <Column field="Parova" header={t("komp.parova")} style={{ minWidth: "90px" }} />
                            <Column header={t("komp.iznos")} style={{ minWidth: "130px" }}
                                body={(r) => <strong className="ak-iznos">{iznos(r.Iznos)}</strong>} />
                        </DataTable>
                    </Card>
                </div>
            </div>
        );
    }

    // ══════════════════════════ JEDNA KOMPENZACIJA ══════════════════════════
    return (
        <div className="col-12">
            <Toast ref={toast} /><ConfirmDialog />
            <Tooltip target=".export-buttons>button" position="bottom" />
            <div className="faktureStranica">

                <div className="iz-traka">
                    <Button type="button" label={t("komp.nazad")} icon="pi pi-arrow-left"
                        className="p-button-sm p-button-text"
                        onClick={() => { setPrikaz("PREGLED"); ucitajPregled(); }} />
                    <Button type="button" label={t("common.sacuvaj")} icon="pi pi-save"
                        className="p-button-sm p-button-success"
                        loading={cuva} disabled={zakljucana} onClick={sacuvaj} />
                    <Button type="button" label={t("komp.izvrsi")} icon="pi pi-check-circle"
                        className="p-button-sm"
                        disabled={!zaglavlje.id || zakljucana || parovi.length === 0} onClick={izvrsi} />
                    <Button type="button" label={t("komp.ponisti")} icon="pi pi-undo"
                        className="p-button-sm p-button-outlined"
                        disabled={!zakljucana} onClick={ponisti} />
                    <Button type="button" label={t("komp.stampa")} icon="pi pi-print"
                        className="p-button-sm p-button-outlined"
                        disabled={!zaglavlje.id} onClick={() => stampaj()} />
                    <Button type="button" label={t("common.delete")} icon="pi pi-trash"
                        className="p-button-sm p-button-danger p-button-outlined"
                        disabled={!zaglavlje.id || zakljucana} onClick={obrisi} />
                    {zaglavlje.statusNaziv && (
                        <span className="sef-pilula sef-st-info">{zaglavlje.statusNaziv}</span>
                    )}
                </div>

                {/* ── zaglavlje ── */}
                <Card>
                    <p className="iz-podnaslov">{t("komp.zaglavlje")}</p>
                    <div className="iz-zaglavlje-grupe">
                        <div className="iz-grupa iz-grupa-izvod">
                            <div className="iz-red">
                                {/* Broj dodeljuje procedura iz brojčanika — polje je samo
                                    za prikaz. Ručno prekucavanje bi napravilo rupu ili
                                    duplikat u nizu, koji je jedinstven po firmi i godini. */}
                                <div className="iz-polje iz-polje-s">
                                    <label>{t("komp.broj")}:</label>
                                    <InputNumber value={zaglavlje.broj} useGrouping={false} readOnly disabled
                                        placeholder={t("komp.brojSeDodeljuje")} />
                                </div>
                                <div className="iz-polje iz-polje-xs">
                                    <label>{t("izvod.godina")}:</label>
                                    <InputNumber value={zaglavlje.godina} useGrouping={false} disabled={zakljucana}
                                        onValueChange={(e) => setZaglavlje((p) => ({ ...p, godina: e.value }))} />
                                </div>
                            </div>
                            <div className="iz-red">
                                <div className="iz-polje iz-polje-m">
                                    <label>{t("izvod.datum")}: <span className="iz-obavezno">*</span></label>
                                    <Calendar value={zaglavlje.datum} dateFormat="dd.mm.yy." showIcon disabled={zakljucana}
                                        onChange={(e) => setZaglavlje((p) => ({ ...p, datum: e.value }))} />
                                </div>
                                <div className="iz-polje iz-polje-s">
                                    <label>{t("ulazne.valuta")}:</label>
                                    <CustomDropdown value={zaglavlje.valuta} optionLabel="label"
                                        options={sifarnici.ValuteDB || []} disabled={zakljucana}
                                        placeholder={t("ulazne.phValuta")}
                                        onChange={(e) => setZaglavlje((p) => ({ ...p, valuta: e.target.value }))} />
                                </div>
                            </div>
                        </div>

                        <div className="iz-grupa iz-grupa-racun">
                            <div className="iz-red">
                                <div className="iz-polje iz-polje-duplo">
                                    <label>{t("komp.partnerPotrazujemo")}: <span className="iz-obavezno">*</span></label>
                                    <CustomDropdown value={zaglavlje.id_klijent} optionLabel="label" filter
                                        options={sifarnici.KlijentiDB || []} disabled={zakljucana}
                                        placeholder={t("ulazne.phDobavljac")}
                                        onChange={(e) => setZaglavlje((p) => ({ ...p, id_klijent: e.target.value }))} />
                                </div>
                            </div>
                            <div className="iz-red">
                                {/* Treća strana se popunjava samo kad dugujemo nekom drugom —
                                    tada je kompenzacija tropartitna i izjavu potpisuju tri. */}
                                <div className="iz-polje iz-polje-duplo">
                                    <label>{t("komp.partnerDugujemo")}:</label>
                                    <CustomDropdown value={zaglavlje.id_klijent_druga_strana} optionLabel="label" filter showClear
                                        options={sifarnici.KlijentiDB || []} disabled={zakljucana}
                                        placeholder={t("komp.istiPartner")}
                                        onChange={(e) => setZaglavlje((p) => ({ ...p, id_klijent_druga_strana: e.target.value }))} />
                                </div>
                            </div>
                            <div className="iz-red">
                                <div className="iz-polje iz-polje-duplo">
                                    <label>{t("komp.brojUgovora")}:</label>
                                    <InputText value={zaglavlje.brojUgovora} disabled={zakljucana}
                                        onChange={(e) => setZaglavlje((p) => ({ ...p, brojUgovora: e.target.value }))} />
                                </div>
                            </div>
                        </div>

                        <div className="iz-grupa iz-grupa-napomena">
                            <div className="iz-polje">
                                <label>{t("nova.napomena")}:</label>
                                <InputTextarea value={zaglavlje.napomena || ""} disabled={zakljucana}
                                    onChange={(e) => setZaglavlje((p) => ({ ...p, napomena: e.target.value }))} />
                            </div>
                        </div>
                    </div>
                </Card>

                {/* ── biranje para ── */}
                {!zakljucana && (
                    <Card>
                        <div className="iz-alat">
                            <span className="iz-podnaslov">{t("komp.noviPar")}</span>
                            <div className="iz-izabrana-suma">
                                <label>{t("komp.iznos")}:</label>
                                <InputNumber value={iznosPara} mode="decimal"
                                    minFractionDigits={2} maxFractionDigits={2}
                                    onValueChange={(e) => setIznosPara(e.value)} />
                            </div>
                            <Button type="button" icon="pi pi-link" label={t("komp.dodajPar")}
                                className="p-button-sm p-button-success"
                                disabled={!izborPotrazujemo || !izborDugujemo || !iznosPara}
                                onClick={dodajPar} />
                        </div>

                        <div className="komp-strane">
                            <div className="komp-strana">
                                <p className="iz-podnaslov">{t("komp.potrazujemoKolona")}</p>
                                <DataTable value={potrazujemo} loading={ucitavaStrane} size="small" showGridlines
                                    scrollable scrollHeight="22vh" className="turePregledTable"
                                    emptyMessage={t("komp.nemaOtvorenih")}
                                    selectionMode="single" selection={izborPotrazujemo}
                                    onSelectionChange={(e) => setIzborPotrazujemo(e.value)}>
                                    <Column field="Broj" header={t("izvod.brojFakture")} style={{ minWidth: "130px" }} />
                                    <Column field="Klijent" header={t("izvod.klijent")} style={{ minWidth: "170px" }} />
                                    <Column field="Datum" header={t("izvod.datum")} style={{ minWidth: "105px" }} />
                                    <Column header={t("izvestaj.otvoreno")} style={{ minWidth: "120px" }}
                                        body={(r) => <span className="ak-iznos">{iznos(r.Otvoreno)}</span>} />
                                    <Column header={t("komp.raspolozivo")} style={{ minWidth: "120px" }}
                                        body={(r) => <strong className="ak-iznos">{iznos(r.Raspolozivo)}</strong>} />
                                </DataTable>
                            </div>

                            <div className="komp-strana">
                                <p className="iz-podnaslov">{t("komp.dugujemoKolona")}</p>
                                <DataTable value={dugujemo} loading={ucitavaStrane} size="small" showGridlines
                                    scrollable scrollHeight="22vh" className="turePregledTable"
                                    emptyMessage={t("komp.nemaOtvorenih")}
                                    selectionMode="single" selection={izborDugujemo}
                                    onSelectionChange={(e) => setIzborDugujemo(e.value)}>
                                    <Column field="Broj" header={t("izvod.brojFakture")} style={{ minWidth: "130px" }} />
                                    <Column field="Klijent" header={t("izvod.klijent")} style={{ minWidth: "170px" }} />
                                    <Column field="Datum" header={t("izvod.datum")} style={{ minWidth: "105px" }} />
                                    <Column header={t("izvestaj.otvoreno")} style={{ minWidth: "120px" }}
                                        body={(r) => <span className="ak-iznos">{iznos(r.Otvoreno)}</span>} />
                                    <Column header={t("komp.raspolozivo")} style={{ minWidth: "120px" }}
                                        body={(r) => <strong className="ak-iznos">{iznos(r.Raspolozivo)}</strong>} />
                                </DataTable>
                            </div>
                        </div>
                    </Card>
                )}

                {/* ── parovi ── */}
                <Card>
                    <p className="iz-podnaslov">{t("komp.parovi")}</p>
                    <DataTable value={parovi} size="small" showGridlines
                        scrollable scrollHeight="28vh" className="turePregledTable"
                        resizableColumns columnResizeMode="expand"
                        emptyMessage={t("komp.nemaParova")}
                        footerColumnGroup={parovi.length ? footerParova : null}>
                        <Column field="BrojPotrazujemo" header={t("komp.potrazujemoKolona")} style={{ minWidth: "140px" }} />
                        <Column field="PartnerPotrazujemo" header={t("komp.partner")} style={{ minWidth: "180px" }} />
                        <Column field="BrojDugujemo" header={t("komp.dugujemoKolona")} style={{ minWidth: "140px" }} />
                        <Column field="PartnerDugujemo" header={t("komp.partner")} style={{ minWidth: "180px" }} />
                        <Column header={t("komp.iznos")} style={{ minWidth: "130px" }}
                            body={(r) => <strong className="ak-iznos">{iznos(r.Iznos)}</strong>} />
                        <Column field="Napomena" header={t("nova.napomena")} style={{ minWidth: "150px" }} />
                        <Column header="" style={{ width: "110px" }}
                            body={(r) => (
                                <Button type="button" icon="pi pi-trash" label={t("common.delete")}
                                    className="p-button-text p-button-sm p-button-danger"
                                    disabled={zakljucana} onClick={() => obrisiPar(r)} />
                            )} />
                    </DataTable>
                </Card>
            </div>
        </div>
    );
};

export default Kompenzacije;
